export interface NafChemistryTitrationInputs {
  mudUsedVolume: number;
  alkalineDemand: number;
  acidVolume: number;
  silverNitrateVolume: number;
  edtaVolume: number;
  silverNitrateConcentration: number;
  edtaConcentration: number;
}

export interface NafChemistryTitrationResults {
  nafMudAlkalinity: number;
  limeContent: number;
  edtaToMudRatio: number;
  silverNitrateToMudRatio: number;
  acidToMudRatio: number;
  wholeMudChlorides: number | null;
  wholeMudCalcium: number | null;
}

export function calculateNafChemistryTitration(
  inputs: NafChemistryTitrationInputs
): NafChemistryTitrationResults | null {
  const {
    mudUsedVolume,
    alkalineDemand,
    acidVolume,
    silverNitrateVolume,
    edtaVolume,
    silverNitrateConcentration,
    edtaConcentration
  } = inputs;

  if (
    ![
      mudUsedVolume,
      alkalineDemand,
      acidVolume,
      silverNitrateVolume,
      edtaVolume,
      silverNitrateConcentration,
      edtaConcentration
    ].every(Number.isFinite)
    || mudUsedVolume <= 0
  ) {
    return null;
  }

  const nafMudAlkalinity = (alkalineDemand + acidVolume) / mudUsedVolume;
  const wholeMudChlorides = silverNitrateConcentration > 0
    ? (silverNitrateVolume * silverNitrateConcentration) / mudUsedVolume
    : null;
  const wholeMudCalcium = edtaConcentration > 0
    ? (edtaVolume * edtaConcentration) / mudUsedVolume
    : null;

  return {
    nafMudAlkalinity,
    limeContent: nafMudAlkalinity * 1.3,
    edtaToMudRatio: edtaVolume / mudUsedVolume,
    silverNitrateToMudRatio: silverNitrateVolume / mudUsedVolume,
    acidToMudRatio: acidVolume / mudUsedVolume,
    wholeMudChlorides: wholeMudChlorides !== null && Number.isFinite(wholeMudChlorides)
      ? wholeMudChlorides
      : null,
    wholeMudCalcium: wholeMudCalcium !== null && Number.isFinite(wholeMudCalcium)
      ? wholeMudCalcium
      : null
  };
}

export function calculateNafWaterPhaseSalinity(
  silverNitrateToMudRatio: number | null,
  edtaToMudRatio: number | null,
  waterVolumePercent: number | null
): number | null {
  const calciumAtomicMass = 40;
  const chlorideAtomicMass = 35.4;

  if (
    silverNitrateToMudRatio === null
    || edtaToMudRatio === null
    || waterVolumePercent === null
    || !Number.isFinite(silverNitrateToMudRatio)
    || !Number.isFinite(edtaToMudRatio)
    || !Number.isFinite(waterVolumePercent)
  ) {
    return null;
  }

  const wholeMudChlorides = 10000 * silverNitrateToMudRatio;
  const calciumConcentration = 4000 * edtaToMudRatio;
  const chlorideFromCalciumChloride = 2 * calciumConcentration * chlorideAtomicMass / calciumAtomicMass;
  const naclCheck = wholeMudChlorides - chlorideFromCalciumChloride;
  const calciumChlorideRecalculated = calciumConcentration === 0
    ? 0
    : naclCheck > 0
      ? 1.57 * chlorideFromCalciumChloride
      : 1.57 * wholeMudChlorides;
  const sodiumChlorideRecalculated = calciumConcentration > 0
    ? 1.65 * naclCheck
    : 1.65 * wholeMudChlorides;
  const waterConcentration = waterVolumePercent * 10000;

  const calciumChlorideDenominator = calciumChlorideRecalculated
    + (naclCheck > 0 ? sodiumChlorideRecalculated : 0)
    + waterConcentration;
  if (calciumChlorideDenominator <= 0 || !Number.isFinite(calciumChlorideDenominator)) return null;

  const calciumChloridePercent = 100 * calciumChlorideRecalculated / calciumChlorideDenominator;
  const sodiumChloridePercent = naclCheck > 0
    ? 100 * sodiumChlorideRecalculated
      / (calciumChlorideRecalculated + sodiumChlorideRecalculated + waterConcentration)
    : 0;
  const maxSodiumChloridePercent = getMaxSodiumChloridePercent(calciumChloridePercent);
  const solubleSodiumChloridePercent = Math.min(sodiumChloridePercent, maxSodiumChloridePercent);
  const waterPhaseSalinity = (calciumChloridePercent + solubleSodiumChloridePercent) * 10000;

  if (sodiumChloridePercent > solubleSodiumChloridePercent) {
    const interpolatedCalciumChloridePercent = interpolateCalciumChloridePercent(
      calciumChlorideRecalculated,
      waterConcentration
    );
    if (interpolatedCalciumChloridePercent === null) return null;

    const alternativeWaterPhaseSalinity = (
      interpolatedCalciumChloridePercent
      + getMaxSodiumChloridePercent(interpolatedCalciumChloridePercent)
    ) * 10000;
    return Number.isFinite(alternativeWaterPhaseSalinity) ? alternativeWaterPhaseSalinity : null;
  }

  return Number.isFinite(waterPhaseSalinity) ? waterPhaseSalinity : null;
}

export interface NafSolidsComposition {
  brineDensity: number;
  brineVolumePercent: number;
  correctedSolidsVolumePercent: number;
  averageSolidsDensity: number;
  highGravitySolidsVolumePercent: number;
  lowGravitySolidsVolumePercent: number;
}

export function calculateNafSolidsComposition(
  silverNitrateToMudRatio: number | null,
  edtaToMudRatio: number | null,
  oilVolumePercent: number | null,
  waterVolumePercent: number | null,
  mudDensity: number | null,
  oilDensity: number | null,
  lowGravitySolidsDensity: number | null,
  weightingMaterialDensity: number | null
): NafSolidsComposition | null {
  const brineCalculation = calculateNafBrineAndCorrectedSolids(
    silverNitrateToMudRatio,
    edtaToMudRatio,
    oilVolumePercent,
    waterVolumePercent
  );

  if (
    brineCalculation === null
    || mudDensity === null
    || oilDensity === null
    || lowGravitySolidsDensity === null
    || weightingMaterialDensity === null
    || !Number.isFinite(mudDensity)
    || !Number.isFinite(oilDensity)
    || !Number.isFinite(lowGravitySolidsDensity)
    || !Number.isFinite(weightingMaterialDensity)
    || mudDensity <= 0
    || oilDensity <= 0
    || lowGravitySolidsDensity <= 0
    || weightingMaterialDensity <= 0
    || brineCalculation.correctedSolidsVolumePercent === 0
    || weightingMaterialDensity === lowGravitySolidsDensity
  ) {
    return null;
  }

  const averageSolidsDensity = (
    (100 * mudDensity)
    - (oilVolumePercent! * oilDensity)
    - (brineCalculation.brineVolumePercent * brineCalculation.brineDensity)
  ) / brineCalculation.correctedSolidsVolumePercent;
  const highGravitySolidsVolumePercent = (
    (averageSolidsDensity - lowGravitySolidsDensity)
    / (weightingMaterialDensity - lowGravitySolidsDensity)
  ) * brineCalculation.correctedSolidsVolumePercent;
  const lowGravitySolidsVolumePercent =
    brineCalculation.correctedSolidsVolumePercent - highGravitySolidsVolumePercent;

  if (
    !Number.isFinite(averageSolidsDensity)
    || !Number.isFinite(highGravitySolidsVolumePercent)
    || !Number.isFinite(lowGravitySolidsVolumePercent)
  ) {
    return null;
  }

  return {
    ...brineCalculation,
    averageSolidsDensity,
    highGravitySolidsVolumePercent,
    lowGravitySolidsVolumePercent
  };
}

export function calculateNafCorrectedSolidsVolumePercent(
  silverNitrateToMudRatio: number | null,
  edtaToMudRatio: number | null,
  oilVolumePercent: number | null,
  waterVolumePercent: number | null
): number | null {
  const calculation = calculateNafBrineAndCorrectedSolids(
    silverNitrateToMudRatio,
    edtaToMudRatio,
    oilVolumePercent,
    waterVolumePercent
  );
  return calculation?.correctedSolidsVolumePercent ?? null;
}

interface NafBrineAndCorrectedSolids {
  brineDensity: number;
  brineVolumePercent: number;
  correctedSolidsVolumePercent: number;
}

function calculateNafBrineAndCorrectedSolids(
  silverNitrateToMudRatio: number | null,
  edtaToMudRatio: number | null,
  oilVolumePercent: number | null,
  waterVolumePercent: number | null
): NafBrineAndCorrectedSolids | null {
  if (
    silverNitrateToMudRatio === null
    || edtaToMudRatio === null
    || oilVolumePercent === null
    || waterVolumePercent === null
    || !Number.isFinite(silverNitrateToMudRatio)
    || !Number.isFinite(edtaToMudRatio)
    || !Number.isFinite(oilVolumePercent)
    || !Number.isFinite(waterVolumePercent)
  ) {
    return null;
  }

  const calciumConcentration = 4000 * edtaToMudRatio;
  const wholeMudChlorides = 10000 * silverNitrateToMudRatio;
  const chlorideFromCalciumChloride = 1.77 * calciumConcentration;
  const naclCheck = wholeMudChlorides - chlorideFromCalciumChloride;
  const calciumChlorideRecalculated = calciumConcentration === 0
    ? 0
    : naclCheck > 0
      ? 1.57 * chlorideFromCalciumChloride
      : 1.57 * wholeMudChlorides;
  const sodiumChlorideRecalculated = calciumConcentration > 0
    ? 1.65 * naclCheck
    : 1.65 * wholeMudChlorides;
  const waterConcentration = 10000 * waterVolumePercent;
  const calciumChlorideDenominator = calciumChlorideRecalculated
    + (naclCheck > 0 ? sodiumChlorideRecalculated : 0)
    + waterConcentration;

  if (calciumChlorideDenominator <= 0 || !Number.isFinite(calciumChlorideDenominator)) return null;

  const calciumChloridePercent = 100 * calciumChlorideRecalculated / calciumChlorideDenominator;
  const sodiumChloridePercent = naclCheck > 0
    ? 100 * sodiumChlorideRecalculated / (calciumChlorideRecalculated + sodiumChlorideRecalculated + waterConcentration)
    : 0;
  const maxSodiumChloridePercent = getMaxSodiumChloridePercent(calciumChloridePercent);
  const solubleSodiumChloridePercent = Math.min(sodiumChloridePercent, maxSodiumChloridePercent);

  let brineDensity: number;
  if (sodiumChloridePercent < 0) {
    brineDensity = 0.99707
      + 0.0079238 * calciumChloridePercent
      + 0.00004964 * calciumChloridePercent * calciumChloridePercent;
  } else if (calciumChloridePercent === 0) {
    brineDensity = 0.99707
      + 0.006504 * solubleSodiumChloridePercent
      + 0.0079238 * calciumChloridePercent
      + 0.00008334 * sodiumChloridePercent * calciumChloridePercent
      + 0.00004395 * solubleSodiumChloridePercent * solubleSodiumChloridePercent
      + 0.00004964 * calciumChloridePercent * calciumChloridePercent;
  } else {
    brineDensity = 0.99707
      + 0.006504 * solubleSodiumChloridePercent
      + 0.0079238 * calciumChloridePercent
      + 0.00008334 * solubleSodiumChloridePercent * calciumChloridePercent
      + 0.00004395 * solubleSodiumChloridePercent * solubleSodiumChloridePercent
      + 0.00004964 * calciumChloridePercent * calciumChloridePercent;
  }

  const brineVolumeDenominator = brineDensity * (
    naclCheck > 0
      ? 100 - calciumChloridePercent - solubleSodiumChloridePercent
      : 100 - calciumChloridePercent
  );
  if (brineVolumeDenominator <= 0 || !Number.isFinite(brineVolumeDenominator)) return null;

  const brineVolumePercent = 100 * waterVolumePercent / brineVolumeDenominator;
  const correctedSolidsVolumePercent = 100 - oilVolumePercent - brineVolumePercent;
  if (
    !Number.isFinite(brineDensity)
    || !Number.isFinite(brineVolumePercent)
    || !Number.isFinite(correctedSolidsVolumePercent)
  ) {
    return null;
  }

  return { brineDensity, brineVolumePercent, correctedSolidsVolumePercent };
}

function interpolateCalciumChloridePercent(
  calciumChlorideConcentration: number,
  waterConcentration: number
): number | null {
  const errorMargin = 0.001;
  let left = 0;
  let right = 100;

  for (let iteration = 0; iteration < 50; iteration++) {
    const middle = (left + right) / 2;
    const sodiumChloridePercent = getMaxSodiumChloridePercent(middle);
    const correctedCalciumPercent = getCalciumChloridePercent(
      calciumChlorideConcentration,
      waterConcentration,
      sodiumChloridePercent
    );

    if (Math.abs(middle - correctedCalciumPercent) <= errorMargin) {
      return correctedCalciumPercent;
    }

    if (correctedCalciumPercent > middle) left = middle;
    else right = middle;
  }

  return null;
}

function getCalciumChloridePercent(
  calciumChlorideConcentration: number,
  waterConcentration: number,
  sodiumChloridePercent: number
): number {
  const denominator = calciumChlorideConcentration + waterConcentration;
  if (denominator === 0) return 0;

  return 100 * calciumChlorideConcentration * (1 - sodiumChloridePercent / 100) / denominator;
}

function getMaxSodiumChloridePercent(calciumChloridePercent: number): number {
  return 26.432
    - 1.0472 * calciumChloridePercent
    + 0.00798191 * calciumChloridePercent * calciumChloridePercent
    + 0.000052238 * calciumChloridePercent * calciumChloridePercent * calciumChloridePercent;
}